import React, { useState, useEffect } from 'react';
import EventCard from '../components/EventCard';
import ProfileNavigation from '../components/ProfileNavigation';
import { userService, eventService } from '../services/api';
import { Event } from '../types/event';
import styles from './SavedEventsPage.module.css';
import { IconBookmark, IconAlertCircle } from '@tabler/icons-react';

const SavedEventsPage: React.FC = () => {
  const [savedEvents, setSavedEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSavedEvents = async () => {
    try {
      setLoading(true);
      const events = await userService.getSavedEvents();
      setSavedEvents(events);
      setError(null);
    } catch (err) {
      console.error('Error fetching saved events:', err);
      setError('Impossible de récupérer vos événements sauvegardés.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSavedEvents();
  }, []);

  const handleInterestClick = async (eventId: number) => {
    try {
      await eventService.toggleInterest(eventId);
      // Mettre à jour l'état local pour refléter le changement
      setSavedEvents(savedEvents.map(event => 
        event.id === eventId 
          ? { ...event, isInterested: !event.isInterested } 
          : event
      ));
    } catch (err) {
      console.error('Error toggling interest:', err);
      setError('Une erreur est survenue lors de la mise à jour de votre intérêt.');
    }
  };

  const handleSaveClick = async (eventId: number) => {
    try {
      await eventService.toggleSave(eventId);
      // Mettre à jour l'état local - supprimer l'événement de la liste si l'utilisateur ne le sauvegarde plus
      // puisque c'est la page des événements sauvegardés
      setSavedEvents(savedEvents.filter(event => event.id !== eventId));
    } catch (err) {
      console.error('Error toggling save:', err);
      setError('Une erreur est survenue lors de la sauvegarde de l\'événement.');
    }
  };

  const handleEventClick = (event: Event) => {
    console.log('Event clicked:', event.id);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>
          <IconBookmark size={24} />
          Mes événements sauvegardés
        </h1>
        <p className={styles.subtitle}>
          Retrouvez tous les événements que vous avez sauvegardés pour y revenir plus tard.
        </p>
      </div>

      <ProfileNavigation />

      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p className={styles.loadingText}>Chargement de vos événements sauvegardés...</p>
        </div>
      ) : error ? (
        <div className={styles.errorContainer}>
          <IconAlertCircle size={24} />
          <p className={styles.errorText}>{error}</p>
        </div>
      ) : savedEvents.length === 0 ? (
        <div className={styles.emptyContainer}>
          <IconBookmark size={64} className={styles.emptyIcon} />
          <h2 className={styles.emptyTitle}>Aucun événement sauvegardé</h2>
          <p className={styles.emptyText}>
            Vous n'avez pas encore sauvegardé d'événements. Explorez les événements disponibles et sauvegardez ceux qui vous intéressent !
          </p>
        </div>
      ) : (
        <div className={styles.eventsGrid}>
          {savedEvents.map((event) => (
            <EventCard 
              key={event.id} 
              event={event} 
              onInterestClick={() => handleInterestClick(event.id)}
              onSaveClick={() => handleSaveClick(event.id)}
              onClick={() => handleEventClick(event)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SavedEventsPage;
